import React from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import classes from './HeaderComponent.module.scss';
import { InputRef, Input, Tooltip, Button, Breadcrumb } from 'antd';
import { useTranslation } from 'react-i18next';
import { CloseCircleFilled, EditOutlined, SaveOutlined } from '@ant-design/icons';
import api from 'api';
import dayjs from 'dayjs';
import { setRooms } from 'store/slices/userDataSlice';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import { setCurrentFolder, setSelectedDocument, setSelectedFolder } from 'store/slices/dataDocumentsSlice';
import { setIsRSidebarCollapsed, setMoreInfoHidden } from 'store/slices/windowStateSlice';
import { useNavigate } from 'react-router';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import { RoomNameExistsModal } from 'components/Modals';
import checkIsRoomNameExists from 'helpers/checkIsRoomNameExists';
import { handleInputChangeWithAltFilter, handlePasteWithAltFilter } from 'helpers/filterAltCodes';
import { createSettingLog } from 'helpers/createSettingLog';
import FileStatuses from 'components/FileStatuses/FileStatuses';
import ContentSearch from 'components/ContentSearch/ContentSearch';

type NameAndDescriptionType = { };

const NameAndDescription = ({}: NameAndDescriptionType) => {
  const [roomName, setRoomName] = React.useState('');
  const [editingMode, setEditingMode] = React.useState(false);
  const [width, setWidth] = React.useState(0);
  const [isChanging, setIsChanging] = React.useState(false);
  const [isRoomNameExistsModalOpen, setIsRoomNameExistsModalOpen] = React.useState(false);  
  const inputRef = React.useRef<InputRef>(null);
  let blurTimer: NodeJS.Timeout;

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const { viewType, currentFolder, selectedDocument, selectedVersion } = useAppSelector((state) => state.documents);
  const { selectedKeys, isFilesSearchingMode, messageApi } = useAppSelector((state) => state.windowState);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { userPermissions, available_rooms, userData } = useAppSelector((store) => store.userData);

  const addonWidth = 100;
  const showSearch = selectedKeys[0] === 'documents' && viewType === 'Table';
  const canEditRoomName = userPermissions?.can_edit_room_settings && dataRoom?.access_terms_status !== 'waiting_for_approve' && !dataRoom?.is_blocked;
  const documentNameToDisplay = selectedDocument?.deleted_at
    ? `(${t('Chats.deletedFile')}) ${selectedDocument?.name}`
    : selectedDocument?.name

  React.useEffect(() => {
    if (editingMode) inputRef.current?.focus();
  }, [editingMode]);

  React.useEffect(() => {
    dataRoom?.name && setRoomName(dataRoom?.name);
    setEditingMode(false);
  },[dataRoom]);

  const checkDuplicateAndOpenModal = (newName: string) => {
    const isDuplicate = checkIsRoomNameExists(newName, available_rooms, userData, dataRoom?.id);
    if (isDuplicate) setIsRoomNameExistsModalOpen(true);
    return isDuplicate;
  };

  const onChangeRoomName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleInputChangeWithAltFilter(e, (filteredValue) => {
      setRoomName(filteredValue);
      setWidth(filteredValue.length * 10 + addonWidth);
    });
  };

  const onPasteRoomName = (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handlePasteWithAltFilter(e, roomName, (filteredValue) => {
      setRoomName(filteredValue);
      setWidth(filteredValue.length * 10 + addonWidth);
    });
  };

  const startEditing = () => {
    if (canEditRoomName) {
      dataRoom?.name && setWidth(addonWidth + dataRoom?.name.length * 10);
      setEditingMode(true);
    }
  };

  const returnFromEditing = () => {
    setRoomName(dataRoom?.name!);
    setEditingMode(false);
  };

  const changeRoomName = async (e: React.MouseEvent<HTMLElement>) => {
    clearTimeout(blurTimer);
    e.stopPropagation();
    e.preventDefault();
    const isDuplicate = checkDuplicateAndOpenModal(roomName);
    if (isDuplicate) {
      return;
    }
    setIsChanging(true);
    try {
      const formDataSettings = new FormData() as any;
      const trimedRoomName = roomName.trim();
      formDataSettings.append('name', trimedRoomName);
      formDataSettings.append('details', JSON.stringify(createSettingLog(dataRoom!, {...dataRoom!, name: trimedRoomName})));

      const response = await api.setRoomSettings(
        formDataSettings,
        dataRoom?.id!,
        'update',
        ''
      );

      const UTCDate = dayjs().valueOf() + new Date().getTimezoneOffset() * 60 * 1000;
      const newRooms = available_rooms.map((room) =>
        room.id === response.data.id
          ? {
            ...response.data,
            created_at: dayjs(response.data.created_at).unix() * 1000,
            last_action_timestamp: UTCDate,
            permissions: room.permissions,
            rooms_constraints: { ...room.rooms_constraints ,members_amount: room.rooms_constraints.members_amount },
            documents_amount: room.documents_amount,
            new_count: room.new_count,
            is_admin: room.is_admin,

          }
          : room
      ).sort((a,b) => b.last_action_timestamp - a.last_action_timestamp);

      dispatch(setRooms(newRooms || []));
      dispatch(setDataRoom({...dataRoom!, name: trimedRoomName}));

      messageApi?.success(
        `${t('Settings.success.settingsUploaded.before')} ${response.data?.name}
        ${t('Settings.success.settingsUploaded.after')}`
      );
    } catch(e) {
      messageApi?.error(
        `${t('Settings.error.settingsUpload.before')} ${dataRoom?.name} 
        ${t('Settings.error.settingsUpload.after')}`
      );
    } finally {
      setIsChanging(false);
    }
  };

  const buttonSave = (
    <Tooltip title={t('sidebar.saveChanges')}>
      <Button
        icon={<SaveOutlined className={classes.saveButton}/>}
        type='link'
        loading={isChanging}
        size='small'
        onClick={changeRoomName}
      />
    </Tooltip>
  );

  const buttonCancel = (
    <Button
      icon={<CloseCircleFilled className={classes.cancelButton}/>}
      type='link'
      size='small'
      onClick={returnFromEditing}
    />
  );

  const openRightSidebar = (section: string) => {
    dispatch(setIsRSidebarCollapsed({
      collapsed: false,
      entity: section,
      width: 350,
    }));
  }

  const breadcrumbItems = React.useMemo(() => {
    const onNavigate = (idx: number) => {
      const slicedFolders = currentFolder.slice(0, idx);      
      localStorage.setItem('previousFolderPath', JSON.stringify(slicedFolders));
      dispatch(setSelectedDocument(null));
      dispatch(setSelectedFolder(null));
      dispatch(setMoreInfoHidden(true));
      dispatch(setCurrentFolder(slicedFolders));
      navigate(`/room/${dataRoom?.id}/documents/${slicedFolders[slicedFolders.length - 1]?.id || ''}`);
    };

    const breadcrumbs = [{ name: '' }, ...currentFolder].map((folder, idx) => {
      const name = idx ? folder?.name : t('Documents.management.rootFolder');
      return {
        title: (<div className={classes.entityName} onClick={() => onNavigate(idx)}>
          <Tooltip placement='bottomLeft' mouseEnterDelay={1} title={name}>
            {name}
          </Tooltip>
        </div>),
      };
    });

    return breadcrumbs;
  }, [currentFolder, dataRoom, i18n.language]);

  const isDocHaveSomeStatus = selectedDocument?.is_new || selectedDocument?.signing_status === 0 || selectedDocument?.signing_status === 1
    || selectedDocument?.signing_status === 2 || selectedDocument?.agreement_status === 0 || selectedDocument?.agreement_status === 1 
    || selectedDocument?.agreement_status === 2 || selectedVersion?.id;  

  const type = availableIconFormat.includes(selectedDocument?.extension || '') ? selectedDocument?.extension : 'default';
  const icon = type as keyof typeof documnentIcons;

  const setBlurTimer = () => {
    blurTimer = setTimeout(returnFromEditing, 100);
  };

  const displayedContent = {
    Table: (<div className={selectedKeys[0] === 'documents' ? classes.nameAndCrumbs : classes.centred}>
      <div
        className={classes.name}
        onBlur={setBlurTimer}
      > 
        {editingMode
          ? (
            <Input
              className={classes.changeInput}
              style={{width: width}}
              ref={inputRef}
              value={roomName}
              onChange={onChangeRoomName}
              onPaste={onPasteRoomName}
              count={{
                show: true,
                max: 80,
              }}
              maxLength={80}
              variant={'borderless'}
              addonAfter={<>
                {(roomName !== dataRoom?.name && roomName.length) ? buttonSave : <span/>}
                {!isChanging && buttonCancel}
              </>}
            />
          )
          : (

            <div className={classes.nameSearchWrapper}>
              <div className={classes.blockName}>
                <Tooltip mouseEnterDelay={.5} title={dataRoom?.name} placement='bottomLeft' styles={{ root: { maxWidth: 'calc(100vw - 400px)' } }}>
                  <div className={classes.ellipsis} onClick={startEditing}>{dataRoom?.name}</div>
                </Tooltip>
                <Button
                  className={canEditRoomName ? classes.editButton : classes.hideButton}
                  icon={<EditOutlined className={classes.editIcon}/>}
                  type='link'
                  size='small'
                  onClick={startEditing}
                />
              </div>

              {showSearch && <ContentSearch />}

            </div>
          )
        }
      </div>

      <div className={classes.breadcrumbs}>
        {isFilesSearchingMode ? (
          t('Documents.management.searchMode')
        ) : (
          selectedKeys[0] === 'documents' && <Breadcrumb items={breadcrumbItems} separator=' > ' />
        )}
      </div>
    </div>),
    Document: (
      <div className={isDocHaveSomeStatus ? classes.nameAndCrumbs : classes.centred}>
        <div className={classes.nameDoc}>
          <div className={classes.modalTitleIcon}>{documnentIcons[icon]}</div>


          <Tooltip
            mouseEnterDelay={.5}
            title={selectedVersion?.name || documentNameToDisplay}
            placement='bottomLeft'
            styles={{ root: { maxWidth: 'calc(100vw - 400px)' } }}
          >
            <div className={classes.ellipsis}>{selectedVersion?.name || documentNameToDisplay}</div>
          </Tooltip>
        </div>

        <FileStatuses
          file={selectedDocument!}
          onSectionIconClick={openRightSidebar}
          viewMode={true}
          version={selectedVersion}
        />
      </div>
    ),
    Report: <div className={classes.nameReport}>{t('sidebar.report')}</div>,
  };
  
  return (
    <>
      {displayedContent[viewType]}
      {isRoomNameExistsModalOpen && (
        <React.Suspense fallback={<div />}>
          <RoomNameExistsModal
            isOpen={isRoomNameExistsModalOpen}
            onCancel={() => setIsRoomNameExistsModalOpen(false)}
            name={roomName}
          />
        </React.Suspense>
      )}
    </>
  );
}

export default NameAndDescription;
